import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Link2,
  Link2Off,
  Copy,
  Check,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  Plug,
  Facebook,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth/AuthProvider';
import {
  getGHLConfig,
  connectGHL,
  disconnectGHL,
  triggerGHLSync,
  getGHLSyncStatus,
  getMetaStatus,
  getMetaConnectUrl,
  MetaNotConfiguredError,
  type GHLConfig,
  type SyncStatus,
  type MetaStatus,
} from '@/lib/api/integrations';

const POLL_INTERVAL_MS = 2000;

function SyncProgressBar({ status }: { status: SyncStatus }) {
  const { progress } = status;
  const pct = progress.percent;

  if (status.status === 'idle') return null;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">
          {progress.processed.toLocaleString()} / {progress.total_contacts > 0 ? progress.total_contacts.toLocaleString() : '...'} contacts
        </span>
        <span className="font-medium">{pct.toFixed(1)}%</span>
      </div>
      <div className="w-full bg-muted rounded-full h-2">
        <div
          className="bg-primary h-2 rounded-full transition-all duration-300"
          style={{ width: `${Math.min(pct, 100)}%` }}
        />
      </div>
      <div className="flex gap-4 text-xs text-muted-foreground">
        <span><span className="text-green-600 font-medium">{progress.created}</span> created</span>
        <span><span className="text-blue-600 font-medium">{progress.updated}</span> updated</span>
        <span><span className="text-muted-foreground font-medium">{progress.skipped}</span> skipped</span>
      </div>
    </div>
  );
}

function ConnectForm({
  tenantId,
  onConnected,
}: {
  tenantId: string;
  onConnected: (config: GHLConfig) => void;
}) {
  const [locationId, setLocationId] = useState('');
  const [pitToken, setPitToken] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConnect = async () => {
    if (!locationId.trim() || !pitToken.trim()) {
      setError('Location ID and PIT Token are required.');
      return;
    }
    setError(null);
    setConnecting(true);
    try {
      const result = await connectGHL(tenantId, locationId.trim(), pitToken.trim());
      toast.success(`Connected to ${result.location_name}`);
      // Reload config
      const newConfig = await getGHLConfig(tenantId);
      onConnected(newConfig);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to connect');
    } finally {
      setConnecting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="location-id">GHL Location ID</Label>
        <Input
          id="location-id"
          placeholder="e.g. AbCdEfGhIj1234567890"
          value={locationId}
          onChange={e => setLocationId(e.target.value)}
          disabled={connecting}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="pit-token">PIT Token</Label>
        <Input
          id="pit-token"
          type="password"
          placeholder="pit_xxxxxxxxxxxxxxxx"
          value={pitToken}
          onChange={e => setPitToken(e.target.value)}
          disabled={connecting}
        />
        <p className="text-xs text-muted-foreground">
          Find this in your GHL sub-account → Settings → Private Integrations.
        </p>
      </div>
      {error && (
        <div className="flex items-start gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3">
          <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
      <Button onClick={handleConnect} disabled={connecting} className="w-full">
        {connecting ? (
          <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Validating &amp; Connecting...</>
        ) : (
          <><Plug className="h-4 w-4 mr-2" /> Connect</>
        )}
      </Button>
    </div>
  );
}

function WebhookUrlField({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-2">
      <Label>Webhook URL</Label>
      <div className="flex gap-2">
        <Input value={url} readOnly className="font-mono text-xs" />
        <Button variant="outline" size="icon" onClick={handleCopy} title="Copy URL">
          {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        Paste this URL into your GHL location's webhook settings to receive real-time contact updates.
        The <code className="text-xs bg-muted px-1 rounded">secret</code> parameter is per-tenant and rotates when you reconnect.
      </p>
    </div>
  );
}

function ConnectedState({
  config,
  tenantId,
  onDisconnected,
}: {
  config: GHLConfig;
  tenantId: string;
  onDisconnected: () => void;
}) {
  const [syncStatus, setSyncStatus] = useState<SyncStatus | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const fetchStatus = useCallback(async () => {
    try {
      const s = await getGHLSyncStatus(tenantId);
      setSyncStatus(s);
      if (s.status !== 'running') {
        stopPolling();
        setSyncing(false);
      }
    } catch {
      stopPolling();
      setSyncing(false);
    }
  }, [tenantId, stopPolling]);

  const startPolling = useCallback(() => {
    fetchStatus();
    pollRef.current = setInterval(fetchStatus, POLL_INTERVAL_MS);
  }, [fetchStatus]);

  // Load initial sync status
  useEffect(() => {
    fetchStatus();
    return stopPolling;
  }, [fetchStatus, stopPolling]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await triggerGHLSync(tenantId);
      toast.success('Sync started');
      startPolling();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to start sync');
      setSyncing(false);
    }
  };

  const handleDisconnect = async () => {
    if (!confirm('Disconnect GHL? This will clear your credentials and webhook secret.')) return;
    setDisconnecting(true);
    stopPolling();
    try {
      await disconnectGHL(tenantId);
      toast.success('GHL disconnected');
      onDisconnected();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to disconnect');
      setDisconnecting(false);
    }
  };

  const showSyncProgress = syncing || syncStatus?.status === 'running';
  const syncCompleted = syncStatus?.status === 'completed';
  const syncFailed = syncStatus?.status === 'failed';

  const lastSyncInfo = syncStatus?.last_sync || config.last_sync;

  return (
    <div className="space-y-6">
      {/* Connected header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Badge className="bg-green-500/20 text-green-700 border-green-500/30 hover:bg-green-500/20">
            <CheckCircle className="h-3 w-3 mr-1" /> Connected
          </Badge>
          {config.location_name && (
            <span className="text-sm font-medium">{config.location_name}</span>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="text-muted-foreground hover:text-red-600"
          onClick={handleDisconnect}
          disabled={disconnecting}
        >
          {disconnecting ? (
            <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
          ) : (
            <Link2Off className="h-4 w-4 mr-1" />
          )}
          Disconnect
        </Button>
      </div>

      {/* Sync section */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium">Contact Sync</p>
            {lastSyncInfo && (
              <p className="text-xs text-muted-foreground">
                Last sync: {new Date(lastSyncInfo.completed_at).toLocaleString()} —{' '}
                {lastSyncInfo.contacts_synced.toLocaleString()} contacts
              </p>
            )}
            {!lastSyncInfo && <p className="text-xs text-muted-foreground">No sync recorded yet</p>}
          </div>
          <Button
            size="sm"
            onClick={handleSync}
            disabled={syncing || syncStatus?.status === 'running'}
          >
            {syncing || syncStatus?.status === 'running' ? (
              <><RefreshCw className="h-4 w-4 mr-1 animate-spin" /> Syncing...</>
            ) : (
              <><RefreshCw className="h-4 w-4 mr-1" /> Sync Contacts</>
            )}
          </Button>
        </div>

        {showSyncProgress && syncStatus && (
          <SyncProgressBar status={syncStatus} />
        )}

        {syncCompleted && syncStatus && (
          <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-md p-3">
            <CheckCircle className="h-4 w-4 flex-shrink-0" />
            <span>
              Sync complete — {syncStatus.progress.created} created,{' '}
              {syncStatus.progress.updated} updated,{' '}
              {syncStatus.progress.skipped} skipped
            </span>
          </div>
        )}

        {syncFailed && syncStatus && (
          <div className="flex items-start gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3">
            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <div>
              <p>Sync failed: {syncStatus.error || 'Unknown error'}</p>
              <Button
                variant="link"
                size="sm"
                className="h-auto p-0 text-red-600 mt-1"
                onClick={handleSync}
              >
                Retry
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Webhook URL */}
      {config.webhook_url && <WebhookUrlField url={config.webhook_url} />}

      {config.connected_at && (
        <p className="text-xs text-muted-foreground">
          Connected {new Date(config.connected_at).toLocaleString()}
        </p>
      )}
    </div>
  );
}

function MetaConnectCard() {
  const [status, setStatus] = useState<MetaStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [notConfigured, setNotConfigured] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    setNotConfigured(false);
    setError(null);
    try {
      const s = await getMetaStatus();
      setStatus(s);
    } catch (e) {
      if (e instanceof MetaNotConfiguredError) {
        setNotConfigured(true);
      } else {
        // status never 503s, but be defensive; treat as not connected.
        setStatus({ connected: false, page_id: null, page_name: null, connected_at: null });
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const handleConnect = async () => {
    setConnecting(true);
    setError(null);
    setNotConfigured(false);
    try {
      const { connect_url } = await getMetaConnectUrl();
      // Redirect the browser to the Facebook OAuth dialog. Meta returns to our
      // /integrations/meta/callback with ?code=...&state=...
      window.location.assign(connect_url);
    } catch (e) {
      if (e instanceof MetaNotConfiguredError) {
        setNotConfigured(true);
      } else {
        setError(e instanceof Error ? e.message : 'Failed to start Facebook connect');
      }
      setConnecting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
            <Facebook className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <CardTitle className="text-base">Facebook / Meta</CardTitle>
            <CardDescription>
              Connect your Facebook Page so leads and Messenger conversations flow into the dialer.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-3">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-10" />
          </div>
        ) : notConfigured ? (
          <div className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3">
            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>
              Meta app not configured yet. Connecting becomes available once the
              platform Meta App ID and secret are set.
            </span>
          </div>
        ) : status?.connected ? (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Badge className="bg-green-500/20 text-green-700 border-green-500/30 hover:bg-green-500/20">
                <CheckCircle className="h-3 w-3 mr-1" /> Connected
              </Badge>
              <span className="text-sm font-medium">
                {status.page_name || status.page_id}
              </span>
            </div>
            {status.connected_at && (
              <p className="text-xs text-muted-foreground">
                Connected {new Date(status.connected_at).toLocaleString()}
              </p>
            )}
            <Button variant="outline" onClick={handleConnect} disabled={connecting} size="sm">
              {connecting ? (
                <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Redirecting...</>
              ) : (
                <><RefreshCw className="h-4 w-4 mr-2" /> Reconnect</>
              )}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-muted-foreground">Not connected</Badge>
            </div>
            {error && (
              <div className="flex items-start gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3">
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}
            <Button onClick={handleConnect} disabled={connecting} className="w-full">
              {connecting ? (
                <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Redirecting to Facebook...</>
              ) : (
                <><Facebook className="h-4 w-4 mr-2" /> Connect Facebook</>
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function IntegrationsPage() {
  const { user } = useAuth();
  const [config, setConfig] = useState<GHLConfig | null>(null);
  const [loading, setLoading] = useState(true);

  const tenantId = user?.tenant_id || '';

  const loadConfig = useCallback(async () => {
    if (!tenantId) return;
    try {
      const cfg = await getGHLConfig(tenantId);
      setConfig(cfg);
    } catch {
      setConfig({ connected: false, location_id: null, location_name: null, webhook_url: null, connected_at: null, last_sync: null });
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  if (user && !user.is_admin && !user.is_super_admin) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card className="border-red-500/50 bg-red-500/10">
          <CardContent className="py-6 text-center text-red-600">
            Admin access required to view this page.
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Link2 className="h-6 w-6" /> Integrations
        </h1>
        <p className="text-muted-foreground">Connect third-party platforms to your dialer.</p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
              <span className="text-orange-700 font-bold text-sm">GHL</span>
            </div>
            <div>
              <CardTitle className="text-base">GoHighLevel</CardTitle>
              <CardDescription>
                Sync contacts from your GHL sub-account and receive real-time webhook updates.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              <Skeleton className="h-8 w-48" />
              <Skeleton className="h-10" />
              <Skeleton className="h-10" />
            </div>
          ) : config?.connected ? (
            <ConnectedState
              config={config}
              tenantId={tenantId}
              onDisconnected={() => {
                setConfig({ connected: false, location_id: null, location_name: null, webhook_url: null, connected_at: null, last_sync: null });
              }}
            />
          ) : (
            <ConnectForm tenantId={tenantId} onConnected={setConfig} />
          )}
        </CardContent>
      </Card>

      <MetaConnectCard />
    </div>
  );
}
